import { useState, useEffect, useRef } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { motion, useAnimationControls } from "framer-motion";

const infiniteBanner = [
  {
    id: 1,
    value: "get book-d",
    color: "#D9ED8C",
  },
  {
    id: 2,
    value: "get busy",
    color: "#8FD3D6",
  },
  {
    id: 3,
    value: "get business",
    color: "#FF8E3C",
  },
];

export const InfinityBanner = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [groups, setGroups] = useState(4); // number of repeated groups rendered inline

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const cw = containerRef.current.offsetWidth;
      setContainerWidth(cw);

      // If we can measure a single group's width, ensure we render enough groups to cover the viewport plus buffer
      const gw = groupRef.current?.scrollWidth ?? 0;
      if (gw > 0) {
        const needed = Math.max(2, Math.ceil(cw / gw) + 2);
        if (needed !== groups) setGroups(needed);
      }
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [groups]);

  useEffect(() => {
    // Animate by exactly one group width for a seamless loop
    const gw = groupRef.current?.scrollWidth ?? 0;
    if (!gw) return;

    const pxPerSecond = 100; // speed
    const duration = gw / pxPerSecond;

    controls.start({
      x: [0, -gw],
      transition: {
        x: {
          duration,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        },
      },
    });
  }, [controls, containerWidth, groups]);

  return (
    <Flex
      ref={containerRef}
      bg="#E3E8EE1A"
      h={{ base: "60px", lg: "87px" }}
      align={"center"}
      px="55px"
      gap={"40px"}
      overflow="hidden" // Hide overflowing content
      whiteSpace="nowrap" // Prevent wrapping
      width="100vw" // Ensure it takes the full viewport width
      position="relative" // Needed for absolute positioning of cloned content (if we were cloning)
    >
      <motion.div
        ref={scrollerRef}
        style={{
          display: "flex",
          gap: "40px",
          width: "fit-content", // Important: make content fit the total width
          flexShrink: 0, // Prevent shrinking
        }}
        initial={{ x: 0 }}
        animate={controls}
      >
        {Array.from({ length: groups }).map((_, gi) => (
          <div
            key={gi}
            ref={gi === 0 ? groupRef : undefined}
            style={{ display: "flex", gap: "40px", flexShrink: 0 }}
          >
            {infiniteBanner.map((item) => (
              <Flex key={`${gi}-${item.id}`} gap={"40px"} flexShrink={0}>
                <Text
                  fontSize={{ base: "16px", lg: "24px" }}
                  fontWeight={500}
                  color={"black"}
                >
                  {item.value}
                </Text>

                <svg width="23" height="27" viewBox="0 0 23 27" fill="none">
                  <path
                    d="M0.499511 13.3C0.389054 13.3 0.299511 13.3895 0.299511 13.5C0.299511 13.6104 0.389054 13.7 0.499511 13.7C6.22962 13.7 11.2995 19.6399 11.2995 26.5C11.2995 26.6105 11.3891 26.7 11.4995 26.7C11.61 26.7 11.6995 26.6105 11.6995 26.5C11.6995 19.6399 16.7694 13.7 22.4995 13.7C22.61 13.7 22.6995 13.6105 22.6995 13.5C22.6995 13.3896 22.61 13.3 22.4995 13.3C16.7694 13.3 11.6995 7.36011 11.6995 0.500001C11.6995 0.389545 11.61 0.300001 11.4995 0.300001C11.3891 0.300001 11.2995 0.389545 11.2995 0.500001C11.2995 7.36011 6.22962 13.3 0.499511 13.3Z"
                    fill={item.color}
                    stroke="#19202D"
                    strokeWidth="0.4"
                    strokeLinejoin="round"
                  />
                </svg>
              </Flex>
            ))}
          </div>
        ))}
      </motion.div>
    </Flex>
  );
};
